'use client';

/**
 * Typed-relative notice (Task C3), shared by /create-comic (style step) and /create-book (Your book step).
 * When the parent's idea names a relative who is not in the photo, Next stops once and asks: use a different photo,
 * or continue without showing them. The choice covers exactly those words; other words ask again.
 */
import React, { useState } from 'react';
import {
  typedRelativeNotice,
  typedRelativesCovered,
  type TypedRelativesChoice,
} from '@/utils/manga/family-guard';
import { trackCreate } from '@/utils/create-flow-analytics';
import type { CreateFlow } from '@/utils/analytics-events';

const wordsKey = (words: string[]) => [...new Set(words.map((w) => w.toLowerCase()))].sort().join('|');

export const CHANGE_PHOTO_LABEL = 'Use a different photo';
export const CONTINUE_LABEL = 'Continue without showing them';

export function useTypedRelativeGate(i: {
  flow: CreateFlow;
  words: string[];
  choice: TypedRelativesChoice | null | undefined;
  onChoiceChange: (choice: TypedRelativesChoice | null) => void;
  onChangePhoto: () => void;
}) {
  const [asked, setAsked] = useState(false);
  // The words the last 'shown' event was sent for (a case-insensitive set key)
  const [shownKey, setShownKey] = useState<string | null>(null);
  const covered = typedRelativesCovered(i.words, i.choice);
  // Editing the idea so no word is left hides the notice (and the next relative typed asks again on Next)
  if (asked && i.words.length === 0) setAsked(false);
  const showing = asked && !covered;
  const key = wordsKey(i.words);
  // Different words while the notice is visible: a new 'shown' (count only)
  if (showing && shownKey !== null && shownKey !== key) {
    setShownKey(key);
    trackCreate(i.flow, 'typed_relative_notice', { action: 'shown', count: i.words.length });
  }

  return {
    showing,
    /** On Next: true when the step may advance; otherwise the notice is shown. */
    allowsNext(): boolean {
      if (covered) return true;
      setAsked(true);
      setShownKey(key);
      trackCreate(i.flow, 'typed_relative_notice', { action: 'shown', count: i.words.length });
      return false;
    },
    changePhoto() {
      trackCreate(i.flow, 'typed_relative_notice', { action: 'change_photo', count: i.words.length });
      setAsked(false);
      i.onChoiceChange(null);
      i.onChangePhoto();
    },
    /** Stores the choice for these words and returns it (the step then advances with it). */
    continueWithout(): TypedRelativesChoice {
      const choice: TypedRelativesChoice = { words: [...i.words], choice: 'continue' };
      trackCreate(i.flow, 'typed_relative_notice', { action: 'continue', count: i.words.length });
      setAsked(false);
      i.onChoiceChange(choice);
      return choice;
    },
  };
}

export function TypedRelativeNotice({
  words,
  onChangePhoto,
  onContinue,
}: {
  words: string[];
  onChangePhoto: () => void;
  onContinue: () => void;
}) {
  return (
    <div role="alert" className="space-y-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p>{typedRelativeNotice(words)}</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onChangePhoto}
          className="rounded-md border border-amber-300 bg-white px-3 py-2 font-medium text-amber-900 hover:bg-amber-100"
        >
          {CHANGE_PHOTO_LABEL}
        </button>
        <button
          type="button"
          onClick={onContinue}
          className="rounded-md bg-purple-600 px-3 py-2 font-medium text-white hover:bg-purple-700"
        >
          {CONTINUE_LABEL}
        </button>
      </div>
    </div>
  );
}
